import axios from 'axios';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  selectIsFirstLogin,
  selectCurrentCreditsNumber,
  selectPlanCreditsNumber,
  selectPlanStatus,
  selectIsNeedToUpdateState,
} from '../../../redux/user-data/selectors';

import {
  changeUserData,
  getUserData,
} from '../../../redux/user-data/operations';

import { setUpdateStatusState } from '../../../redux/user-data/userDataSlice';

import useWindowWidth from 'hooks/useWindowWidth';
import { refreshAxiosInstance } from 'services/axiosConfig';
import handleError from 'services/handleError';

import { COOKIE_DOMAIN_NAME, USER_POOL_CLIENT_ID } from 'utils/configs';

import Logo from 'components/interactiveComponents/Logo';
import { IconButton } from 'components/common/Buttons/Buttons';
import icon from 'media/images/svgSprite.svg';
import ModalLight from 'components/common/modals/ModalLight';
import Modal from 'components/common/modals/Modal';
import MainMenu from 'components/SharedLayout/MainMenu';
import UserMenu from 'components/user/UserMenu';
import BetaLable from 'components/interactiveComponents/BetaLable';
import WelcomeVideo from 'components/WelcomeVideo';

import {
  HeaderWrap,
  HeaderContainer,
  BurgerIconWrap,
  HeaderLogoSection,
  BigLogoWrap,
  SmallLogoWrap,
  NavWrap,
  MenuIcon,
  CreditsWrap,
  CreditsText,
  // Progress,
  UserIcon,
} from 'components/SharedLayout/Header/Header.styled';

const tokenArr = ['accessToken', 'idToken', 'refreshToken'];
const tokenCompare = {
  accessToken: 'access_token',
  idToken: 'id_token',
  refreshToken: 'refresh_token',
};

const Header = () => {
  const [isNewUser, setIsNewUser] = useState(false);
  const [balance, setBalance] = useState({
    current: 0,
    maxPossible: 0,
  });
  // const [usagePercentage, setUsagePercentage] = useState(0);
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState('open');
  const [opacity, setOpacity] = useState(1);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const isFirstLogin = useSelector(selectIsFirstLogin);
  const subscriptionStatus = useSelector(selectPlanStatus);
  const credits = useSelector(selectCurrentCreditsNumber);
  const planCredits = useSelector(selectPlanCreditsNumber);
  const isNeedToUpdateState = useSelector(selectIsNeedToUpdateState);

  const dispatch = useDispatch();
  const width = useWindowWidth();
  const isMobile = width < 767;

  const { user } = useAuthenticator(context => [context.user]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.username) return;

    (async () => {
      tokenArr.forEach(item => {
        const tokenValue = localStorage.getItem(
          `CognitoIdentityServiceProvider.${USER_POOL_CLIENT_ID}.${user?.username}.${item}`
        );

        document.cookie = `${tokenCompare[item]}=${tokenValue};path=/;domain=${COOKIE_DOMAIN_NAME};Secure;Partitioned;SameSite=None`;
      });

      refreshAxiosInstance();

      try {
        await axios.post(`/user/ensure`);
      } catch (error) {
        const { response } = error;
        if (response) handleError(response.status, response.data);
      }
    })();
  }, [user?.username]);

  useEffect(() => {
    // if (subscriptionStatus === null) {
    //   return;
    // }

    setBalance({
      current: credits,
      maxPossible: planCredits ? planCredits : 0,
    });

    // let currentPercent = null;

    // if (planCredits === null) {
    //   currentPercent = 100;
    // } else {
    //   currentPercent = (credits * 100) / planCredits;
    // }

    // setUsagePercentage(currentPercent);
  }, [credits, planCredits, subscriptionStatus]);

  useEffect(() => {
    let checkCreditsNumber = null;
    let checkCounter = 1;

    if (!isNeedToUpdateState.status) return;

    if (isNeedToUpdateState.status) {
      checkCreditsNumber = setInterval(async () => {
        try {
          const { data } = await axios.get(`/user/profile/read`);

          if (
            data.credits !== isNeedToUpdateState.creditsToCompare ||
            checkCounter > 8
          ) {
            dispatch(
              setUpdateStatusState({ creditsToCompare: null, status: false })
            );
            dispatch(getUserData());
            clearInterval(checkCreditsNumber);
          } else {
            checkCounter += 1;
          }

          return;
        } catch (error) {
          let errorDetails = error;
          if (error?.response?.status && error?.response?.data) {
            const { status, data } = error.response;
            errorDetails = handleError(status, data);
          } else {
            console.error('Error:', error.message);
          }

          return errorDetails;
        }
      }, 1000);
    }

    return () => {
      clearInterval(checkCreditsNumber);
    };
  }, [dispatch, isNeedToUpdateState]);

  useEffect(() => {
    if (user?.username && isFirstLogin) {
      setIsNewUser(true);
    } else {
      setIsNewUser(false);
    }
  }, [isFirstLogin, user?.username]);

  function onOpenOrCloseUserMenu() {
    setIsUserMenuOpen(!isUserMenuOpen);
  }

  function onOpenMenu() {
    setIsMenuOpen('open');
    setOpacity(1);
    setIsMainMenuOpen(true);
    setIsUserMenuOpen(false);
  }

  function onCloseMenu() {
    setIsMenuOpen('close');

    setTimeout(() => {
      setOpacity(0);
    }, 290);

    setTimeout(() => {
      setIsMainMenuOpen(false);
    }, 300);
  }

  function OpenCabinetFromUserMenu() {
    setIsUserMenuOpen(false);
    navigate('user-cabinet');
  }

  async function onCloseWelcomeVideo() {
    setIsNewUser(false);
    dispatch(changeUserData({ is_first_login: false }));
  }

  return (
    <>
      <HeaderWrap id="header">
        <HeaderContainer>
          <BurgerIconWrap onClick={onOpenMenu}>
            <MenuIcon>
              <use href={`${icon}#icon-burger-menu_2`}></use>
            </MenuIcon>
          </BurgerIconWrap>

          <HeaderLogoSection>
            {isMobile ? (
              <SmallLogoWrap>
                <Logo iconName={'flower'} />
              </SmallLogoWrap>
            ) : (
              <BigLogoWrap>
                <Logo iconName={'logo'} />
              </BigLogoWrap>
            )}
            <BetaLable
              topLablePos={'4px'}
              rightLablePos={'-23px'}
              topMessagePos={'61px'}
              bottomMessagePos={'auto'}
              leftMessagePos={'-55px'}
              translateXValue={'0'}
              lableLocation={'header'}
            />
          </HeaderLogoSection>

          <NavWrap>
            {/* <CreditsWrap to={'pricing'}>
              {balance.maxPossible === null ? (
                <CreditsText>unlimited credits</CreditsText>
              ) : (
                <CreditsText>
                  {balance.current}/{balance.maxPossible} left
                </CreditsText>
              )}
              <Progress $used={usagePercentage}></Progress>
            </CreditsWrap>  */}
            <CreditsWrap to={'pricing'}>
              <CreditsText>{balance.current} credits left</CreditsText>
            </CreditsWrap>
            <UserIcon>
              <IconButton
                btnColor={'black_transparent'}
                handleClick={onOpenOrCloseUserMenu}
              >
                <MenuIcon>
                  <use href={`${icon}#icon-user`}></use>
                </MenuIcon>
              </IconButton>
            </UserIcon>
          </NavWrap>
        </HeaderContainer>
      </HeaderWrap>

      {isMainMenuOpen && (
        <MainMenu
          isClosing={isMenuOpen}
          isVisible={opacity}
          onCloseMenu={onCloseMenu}
        />
      )}
      {isUserMenuOpen && (
        <ModalLight closeModal={onOpenOrCloseUserMenu}>
          <UserMenu
            closeModal={onOpenOrCloseUserMenu}
            onOpenUserCabinet={OpenCabinetFromUserMenu}
          />
        </ModalLight>
      )}

      {isNewUser && (
        <Modal closeModal={onCloseWelcomeVideo}>
          <WelcomeVideo onCloseWelcomeModal={onCloseWelcomeVideo} />
        </Modal>
      )}
    </>
  );
};

export default Header;
